import React, { useState, useRef } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { SpinLoading } from 'antd-mobile';
import { useQuery } from '@tanstack/react-query';

import { useApi } from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import useNativeApp from '../hooks/useNativeApp';

const ProtectedRoute = ({ requirePermission, element, context = 'user' }) => {
  const { postData } = useApi();
  const { getToken, isTokenRefreshing } = useAuth();
  const { isNativeApp } = useNativeApp();
  const navigate = useNavigate();
  const hasExecutedQuery = useRef(false);

  const [isAuthorized, setIsAuthorized] = useState(false);

  // NOTE: special for check access it will retry 10 times if failed to avoid no access page issue
  const { isLoading: isAccessLoading } = useQuery({
    queryKey: ['checkAccess', requirePermission],
    queryFn: async () => {
      setIsAuthorized(false);
      const jwtToken = await getToken();
      if (!jwtToken) {
        throw new Error('No token available');
      }
      
      const payload = { keys: requirePermission };
      const response = await postData('/api/check-access', payload, {
        headers: {
          Authorization: `Bearer ${jwtToken}`,
          'Content-Type': 'application/json',
        },
      }, context);

      if (response.code === 200) {
        const currentAuthorized = requirePermission.every(
          (perm) => response?.data?.includes(perm)
        );
        setIsAuthorized(currentAuthorized);
        hasExecutedQuery.current = true;
        if (!currentAuthorized) {
          console.log('🔑🔑Access Denied:🔑🔑', requirePermission, response?.data);
          navigate('/no-access');
          console.error(new Error(`Access Denied: user don't have permission ${requirePermission}`));
        }

        return response?.data || [];
      }
      
      return [];
    },
    retry: (failureCount, error) => {
      console.log('🔑🔑Retry:🔑🔑', failureCount, error);

      if (failureCount < 10 && error?.code === "ERR_NETWORK") {
        console.log(`🔄 Retrying... (${failureCount}/10)`);
        return true;
      }

      console.error(`Access Denied on retry ${failureCount}: ${error?.response?.data?.message || error?.message || 'unknown error'}`);
      setIsAuthorized(false);
      navigate('/no-access');
      return false;
    },
    retryDelay: 1000,
    enabled: !isTokenRefreshing && !hasExecutedQuery.current,
  });

  if (isNativeApp && context === 'admin') {
    return <Navigate to="/home" />
  }

  if (isAccessLoading || isTokenRefreshing) {
    return <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}><SpinLoading /></div>
  }

  if (!isAuthorized) {
    return null;
  }

  return element;
};
export default ProtectedRoute;
